/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// errors
import ApiRequestFailError from "errors/ApiRequestFailError.js";
import InvalidTransactionError from "errors/InvalidTransactionError.js";

// orchestration
import Firebase from "orchestration/firebase-library/Firebase.js";

// services
import StorageService from "orchestration/services/storage-service/StorageService.js";

// utils
import ConversionUtils from "utils/conversion-utils/ConversionUtils.js";
import ImageUtils from "utils/image-utils/ImageUtils.js";
import LoggerUtils from "utils/logger-utils/LoggerUtils";
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

class TransactionService {

  /**
  * @async 
  * @param {String} apiPath
  * @param {String} payloadId
  * @throws {ApiRequestFailError}
  * @throws {InvalidTransactionError}
  */
  static async createTransaction(apiPath, payloadId) {
    const transactionApiPath = TransactionService.getTransactionApiPath(apiPath);
    const _payload = { create: true, id: payloadId };
    return TransactionService.requestTransaction(transactionApiPath, _payload);
  }

  /**
  * @async
  * @param {String} apiPath
  * @param {String} transactionId
  * @param {Object} payload
  * @throws {ApiRequestFailError}
  * @throws {InvalidTransactionError}
  */
  static async executeTransaction(apiPath, transactionId, payload) {
    const transactionApiPath = TransactionService.getTransactionApiPath(apiPath);
    const _payload = { execute: true, transactionId, ...payload };
    return TransactionService.requestTransaction(transactionApiPath, _payload);
  }

  /**
  * @param {String} apiPath
  * @returns {String}
  */
  static getTransactionApiPath(apiPath) {
    const cloudFunctionApiPaths = Firebase.CloudFunctions.getApiPaths();
    return cloudFunctionApiPaths[apiPath];
  }

  /**
  * @async
  * @param {String} transactionId
  * @param {Image[]} images
  * @param {Function} onProgress
  * @param {String} fileNamePrefix
  * @throws {InvalidTransactionError}
  */
   static async processImageIngest(transactionId, images, onProgress, fileNamePrefix = "image") {
    if(!ValidationUtils.string(transactionId)) {
      const errorMessage = String(`Invalid transactionId: "${transactionId}".`);
      LoggerUtils.error("[TransactionService] processImageIngest() - InvalidTransactionError", errorMessage);
      throw new InvalidTransactionError(errorMessage);
    }
    
    if(!ValidationUtils.array(images)) {
      const errorMessage = String(`Invalid images: "${images}".`);
      LoggerUtils.error("[TransactionService] processImageIngest() - InvalidTransactionError", errorMessage);
      throw new InvalidTransactionError(errorMessage);
    }

    await Promise.all(Array.from(images).map(async (image, index) => {
      // set image id
      let imageId = image.getId();
      if(String(imageId) === String("new")) {
        imageId = String(transactionId);
      }

      // get image source data
      const imageSources = image.getSources();
      const { full } = imageSources;

      if(
        ValidationUtils.string(full) && (
          String(full).startsWith("data:") ||
          String(full).startsWith("blob:") 
        )
      ) {

        // convert image to blob
        const loadedImage = await ImageUtils.loadImage(full);
        const payload = await ConversionUtils.imageToBlob(loadedImage);

        // remove raw image data from application
        images[index].state.sources = {};
        
        // upload image data to transaction ingest
        const fileName = `${fileNamePrefix}-${imageId}-${index}`;
        return StorageService.upload(
          transactionId,
          fileName,
          payload,
          { contentType: "image/png" },
          onProgress
        );
      }
    }));
  }

  /**
  * @async
  * @param {String} apiPath
  * @param {Object} payload
  * @throws {ApiRequestFailError}
  * @throws {InvalidTransactionError}
  */
  static async requestTransaction(apiPath, payload) {
    if(!ValidationUtils.string(apiPath)) {
      const errorMessage = String(`Invalid apiPath: "${apiPath}".`);
      LoggerUtils.error("[TransactionService] requestTransaction() - InvalidTransactionError", errorMessage);
      throw new InvalidTransactionError(errorMessage);
    }

    if(!ValidationUtils.exists(payload)){
      const errorMessage = String(`Invalid payload: "${JSON.stringify(payload)}".`);
      LoggerUtils.error("[TransactionService] requestTransaction() - InvalidTransactionError", errorMessage);
      throw new InvalidTransactionError(errorMessage);
    }

    const domain = Firebase.getDomain();
    const userToken = Firebase.getUserToken();
    try {
      return Firebase.CloudFunctions.apiRequest(apiPath, domain, payload, userToken);
    }
    catch(error) {
      LoggerUtils.error("[TransactionService] requestTransaction() - ApiRequestFailError", error);
      throw new ApiRequestFailError(error.message);
    }
  }
}
export default TransactionService;
